import styled from '@emotion/styled';
import { CreativeSearchProvider } from '@kaizenplatform/creative-search-ui';
import { createPath } from 'history';
import { ClassAttributes, createElement, useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import { useSearchParam } from 'react-use';

import { OrganizationDetailFragment } from '../../generated/graphql';
import mergeUrlQuery from '../../utility/mergeUrlQuery';
import { context as DependencyContext } from '../context/dependency';
import { context as HistoryContext } from '../context/history';
import { context as StateContext } from '../context/state';
import AdSearchPage from '../page/AdSearchPage';

type Props = ClassAttributes<HTMLElement> & {
  className?: string;
};

const EN = 'en-US';
const LANGUAGE_EN = 'us';
const LOCALE_EN = 'en';
const JP = 'ja-JP';
const LANGUAGE_JP = 'jp';
const LOCALE_JP = 'ja';

const ConnectedAdShowcasePage: React.FC<Props> = () => {
  const state = useContext(StateContext);
  const dependency = useContext(DependencyContext);
  const orgId = useSearchParam('org');

  const getLanguage = () => {
    switch (state.language) {
      case EN:
        return LANGUAGE_EN;
      case JP:
        return LANGUAGE_JP;
      default:
        throw new Error('language is not set');
    }
  };
  const getLocale = () => {
    switch (state.language) {
      case EN:
        return LOCALE_EN;
      case JP:
        return LOCALE_JP;
      default:
        throw new Error('locale is not set');
    }
  };
  const locale = getLocale();
  const language = getLanguage();
  const history = useContext(HistoryContext);

  const [businessRegion, setBusinessRegion] = useState<'us' | 'jp' | 'gl'>();
  const [organization, setOrganization] = useState<OrganizationDetailFragment>();

  useEffect(() => {
    const curOrg =
      state.availableOrganizations.items.find((ao) => ao.id === orgId) || state.availableOrganizations.items[0];
    if (state.lastAccessedOrganization) {
      setOrganization(state.lastAccessedOrganization);
    } else {
      setOrganization(curOrg);
    }
    if (!organization && !curOrg) setBusinessRegion(language);
    if (!orgId) return;
    curOrg && setBusinessRegion(curOrg?.businessRegion.key);
  }, [orgId, state.availableOrganizations, language, state.lastAccessedOrganization, organization]);

  useEffect(() => {
    dependency.adSearchUsecase.setLastAccessedOrg(organization);
  }, [dependency.adSearchUsecase, organization]);

  // クエパラにorganizationIdが存在しない場合はリダイレクトする
  if (!orgId && organization) {
    return <Redirect to={createPath(mergeUrlQuery(history.location, { org: organization.id }))} />;
  }

  if (!businessRegion) return null;

  return (
    businessRegion && (
      <CreativeSearchProvider locale={locale} businessRegion={[businessRegion]}>
        <Page currentOrganization={organization} />
      </CreativeSearchProvider>
    )
  );
};

const Page = styled(AdSearchPage)`
  margin-left: auto;
  margin-right: auto;
  max-width: 1080px;
`;

export default ConnectedAdShowcasePage;
